"use client";

import { useEffect, useState } from "react";
import { ArrowLeftRight, ArrowUpRight, Check, ChevronDown, ChevronLeft, ChevronRight, Share2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { clock } from "@/components/odds-screen/live/time";
import type { DisplayPlayerProp } from "@/components/optimizer/utils-v2";
import type { EventHeader } from "@/lib/odds-screen/live/types";
import type { BestSlips, EnteredSlip, GeneratedSlip, TargetSlips } from "@/lib/fantasy-props/best-slips";
import { slipLink } from "@/lib/fantasy-props/book-links";
import { playerTag } from "@/lib/fantasy-props/player-tag";
import { legFromRow, legHref, legMultiple, payoutToAmerican, slipLegsText, withCurrent, type SlipLegView, type SlipTarget, targetForLeague } from "@/lib/fantasy-props/slip";
import type { OddsFormat } from "@/utils/calculators/odds-utils";
import { evText } from "./board-parts";
import { slipEvClass } from "@/lib/slip/formatters";
import { chipClass, SectionLabel, Stepper } from "./chrome";
import { formatMultiple, formatOdds, formatPct, rowLines } from "./model";
import { capText, PlayLineText, PromoIcon, PromoTag } from "./promo";
import { noStandardText } from "./slip-pricing";
import type { Grading } from "@/lib/fantasy-props/rows";

export type BestSlipsViewProps = {
  book: string;
  grading: Grading;
  result: BestSlips;
  rowAt: (id: string) => DisplayPlayerProp | null;
  events: ReadonlyMap<string, EventHeader>;
  format: OddsFormat;
  minEvPct: number;
  onMinEv: (value: number) => void;
  swappedCount: number;
  onResetSwaps: () => void;
  entered: readonly EnteredSlip[];
  onUse: (slip: GeneratedSlip, target: SlipTarget) => void;
  onEnter: (slip: GeneratedSlip, target: SlipTarget) => void;
  onUnenter: (id: string) => void;
  onSwap: (rowId: string) => void;
  onPreview: (rowIds: readonly string[] | null) => void;
  modelLabel: string;
  boostPct: number;
  oneLeague: boolean;
  isMobile: boolean;
};

const ICON_BUTTON = "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-zinc-500 hover:bg-white/[0.06] hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

function EvPill({ evPct, size = "md", muted = false }: { evPct: number; size?: "sm" | "md"; muted?: boolean }) {
  return (
    <span className={cn("data-num inline-flex items-center rounded-md border font-semibold", size === "md" ? "h-6 px-2 text-[13px]" : "h-[22px] px-1.5 text-[12px]", muted ? "border-white/10 bg-white/5 text-zinc-500" : slipEvClass(evPct))}>
      {evText(evPct)}
    </span>
  );
}

function LeagueTag({ league }: { league: string }) {
  return <span className="flex-shrink-0 rounded bg-white/[0.06] px-1 text-[10px] font-semibold leading-4 tracking-[0.04em] text-zinc-400">{league}</span>;
}

function ShareSlip({ text, tall }: { text: string; tall: boolean }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const timer = window.setTimeout(() => setDone(false), 1200);
    return () => window.clearTimeout(timer);
  }, [done]);
  const share = () => {
    const finish = () => setDone(true);
    if (typeof navigator.share === "function") {
      navigator.share({ text }).then(finish).catch(() => {});
      return;
    }
    navigator.clipboard.writeText(text).then(finish).catch(() => {});
  };
  return (
    <button type="button" onClick={share} aria-label={done ? "Shared" : "Share this slip"} className={cn(chipClass(false, tall), "px-0", tall ? "w-9" : "w-8")}>
      {done ? <Check size={12} strokeWidth={2.5} aria-hidden="true" className="text-primary" /> : <Share2 size={12} strokeWidth={2} aria-hidden="true" />}
    </button>
  );
}

function slipLegViews(slip: GeneratedSlip, book: string, rowAt: (id: string) => DisplayPlayerProp | null, events: ReadonlyMap<string, EventHeader>): SlipLegView[] {
  const legs: SlipLegView[] = [];
  for (const rowId of slip.rowIds) {
    const row = rowAt(rowId);
    if (row === null) continue;
    const event = row.event_id ? events.get(row.event_id) : undefined;
    legs.push(withCurrent(legFromRow(row, book, rowLines(row, event)), row));
  }
  return legs;
}

function summary(slip: GeneratedSlip, format: OddsFormat): string {
  const parlay = payoutToAmerican(slip.payout);
  return [formatMultiple(slip.payout), `${formatPct(slip.hitAllPct)} all hit`, parlay === null ? null : formatOdds(parlay, format)].filter(Boolean).join(" · ");
}

function ExpandedCard({
  entry, page, onPage, book, rowAt, events, format, onUse, onEnter, onSwap, onPreview, tall,
}: {
  entry: TargetSlips;
  page: number;
  onPage: (page: number) => void;
  book: string;
  rowAt: (id: string) => DisplayPlayerProp | null;
  events: ReadonlyMap<string, EventHeader>;
  format: OddsFormat;
  onUse: (slip: GeneratedSlip, target: SlipTarget) => void;
  onEnter: (slip: GeneratedSlip, target: SlipTarget) => void;
  onSwap: (rowId: string) => void;
  onPreview: (rowIds: readonly string[] | null) => void;
  tall: boolean;
}) {
  const { target } = entry;
  const slip = entry.slips[Math.min(page, entry.slips.length - 1)];
  const [hovered, setHovered] = useState(false);
  const slipKey = slip?.id ?? null;
  useEffect(() => {
    if (!hovered || slipKey === null) return;
    onPreview(slip.rowIds);
  }, [hovered, slipKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onPreview(null), []); // eslint-disable-line react-hooks/exhaustive-deps
  if (slip === undefined) return null;
  const legs = slipLegViews(slip, book, rowAt, events);
  const bookName = sportsbookIdToDisplay(book);
  const placeHref = slipLink(book, legs.map((leg) => legHref(leg, tall)));
  return (
    <section
      aria-label={`${target.label}, best slip`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); onPreview(null); }}
      className="mx-1.5 rounded-md border border-primary/35 bg-primary/[0.03]"
    >
      <div className="flex flex-col gap-1 px-3 pb-2 pt-2.5">
        <span className="flex items-center gap-1.5"><span className="text-[13px] font-semibold leading-[18px] text-foreground">{target.label}</span>{slip.league !== null && <LeagueTag league={slip.league} />}{capText(slip.cap) && <PromoTag>{capText(slip.cap)}</PromoTag>}</span>
        <span className="flex items-center gap-2">
        <span className="data-num text-[11px] leading-4 text-zinc-500">{summary(slip, format)}</span>
        <span className="flex-grow" />
        <EvPill evPct={slip.evPct} />
        {entry.slips.length > 1 && (
          <span role="group" aria-label="Other slips" className="flex h-6 items-center gap-0.5 rounded-md bg-white/[0.04] p-0.5">
            <button type="button" aria-label="Previous slip" disabled={page <= 0} onClick={() => onPage(page - 1)} className={cn(ICON_BUTTON, "h-[18px] w-5")}><ChevronLeft size={12} strokeWidth={2.5} aria-hidden="true" /></button>
            <span className="data-num text-[10px] font-semibold text-zinc-400">{Math.min(page, entry.slips.length - 1) + 1}/{entry.slips.length}</span>
            <button type="button" aria-label="Next slip" disabled={page >= entry.slips.length - 1} onClick={() => onPage(page + 1)} className={cn(ICON_BUTTON, "h-[18px] w-5")}><ChevronRight size={12} strokeWidth={2.5} aria-hidden="true" /></button>
          </span>
        )}
        </span>
      </div>
      <ul role="list" aria-label="Legs" className="flex flex-col border-t border-white/[0.06] py-1">
        {legs.map((leg, index) => {
          const hit = leg.current?.hit ?? leg.hit;
          const odds = leg.current?.odds ?? leg.odds;
          const power = leg.promo?.kind === "power" ? legMultiple(leg) : null;
          const tag = playerTag({ player_position: leg.position, team_short: leg.team });
          return (
            <li key={leg.rowId} className="flex items-start gap-2.5 py-2 pl-2.5 pr-1">
              <span className="data-num mt-0.5 flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded bg-white/[0.06] text-[10px] font-semibold text-zinc-400" aria-hidden="true">{index + 1}</span>
              <span className="flex min-w-0 flex-grow flex-col">
                <span className="flex items-center gap-1.5 text-[13px] font-semibold leading-[18px] text-foreground"><span className="truncate">{leg.player}</span>{tag !== null && <span className="flex-shrink-0 text-[11px] font-medium leading-[18px] text-zinc-500">{tag}</span>}{leg.promo !== null && <PromoIcon promo={leg.promo} />}</span>
                <PlayLineText play={leg.play} className="text-[12px] leading-4 text-zinc-400" />
              </span>
              <span className="flex flex-shrink-0 flex-col items-end gap-0.5">
                <span className="data-num inline-flex h-6 items-center rounded-md border border-accent-value/30 bg-accent-value/20 px-1.5 text-[12px] font-semibold text-accent-value">{formatPct(hit)}</span>
                {odds !== null && (
                  <span className="flex items-center gap-1">
                    <span className="data-num text-[12px] font-semibold text-zinc-400">{formatOdds(odds, format)}</span>
                    {power !== null && <PromoTag>{formatMultiple(power)}</PromoTag>}
                  </span>
                )}
              </span>
              <button type="button" onClick={() => onSwap(leg.rowId)} aria-label={`Swap ${leg.player} for the next best leg`} className={ICON_BUTTON}>
                <ArrowLeftRight size={12} strokeWidth={2} aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-1.5 border-t border-white/[0.06] px-2.5 pb-2.5 pt-2">
        <button
          type="button"
          onClick={() => onUse(slip, target)}
          className={cn("tap flex items-center justify-center rounded-md bg-primary px-3 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", tall ? "h-9" : "h-8")}
        >
          Use this slip
        </button>
        <span className="flex-grow" />
        {placeHref !== null && (
          <a href={placeHref} target="_blank" rel="noopener noreferrer" aria-label={`Place this slip on ${bookName}`} className={chipClass(false, tall)}>
            <ArrowUpRight size={12} strokeWidth={2.5} aria-hidden="true" />
            Place
          </a>
        )}
        <button type="button" onClick={() => onEnter(slip, target)} aria-label="Mark this slip entered" className={chipClass(false, tall)}>
          <Check size={12} strokeWidth={2.5} aria-hidden="true" />
          Entered
        </button>
        <ShareSlip text={slipLegsText(legs, bookName, targetForLeague(target, slip.league), slip.payout)} tall={tall} />
      </div>
    </section>
  );
}

function CollapsedRow({ entry, onExpand, tall }: { entry: TargetSlips; onExpand: () => void; tall: boolean }) {
  const { target } = entry;
  const slip = entry.slips[0];
  const muted = entry.status !== "ready";
  const meta = slip === undefined ? `${formatMultiple(target.payout)} · not enough games on the board` : `${formatMultiple(slip.payout)} · ${formatPct(slip.hitAllPct)} all hit`;
  return (
    <button
      type="button"
      aria-expanded={false}
      onClick={onExpand}
      className={cn("mx-1.5 flex items-center gap-2 rounded-md border border-surface-line pl-3 pr-2.5 text-left hover:bg-white/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", tall ? "h-12" : "h-11")}
    >
      <span className="flex min-w-0 flex-grow flex-col">
        <span className="flex items-center gap-1.5"><span className={cn("truncate text-[13px] font-semibold leading-[18px]", muted ? "text-zinc-500" : "text-foreground")}>{target.label}</span>{slip?.league != null && <LeagueTag league={slip.league} />}{slip != null && capText(slip.cap) && <PromoTag>{capText(slip.cap)}</PromoTag>}</span>
        <span className="data-num truncate text-[11px] leading-4 text-zinc-500">{meta}</span>
      </span>
      {slip === undefined ? <span className="text-[11px] text-zinc-500">short</span> : <EvPill evPct={slip.evPct} size="sm" muted={muted} />}
      <ChevronDown size={14} strokeWidth={2} aria-hidden="true" className="flex-shrink-0 text-zinc-500" />
    </button>
  );
}

export function BestSlipsView({
  book, grading, result, rowAt, events, format, minEvPct, onMinEv, swappedCount, onResetSwaps, entered, onUse, onEnter, onUnenter, onSwap, onPreview, modelLabel, boostPct, oneLeague, isMobile,
}: BestSlipsViewProps) {
  const tall = isMobile;
  const [chosen, setChosen] = useState<string | null>(null);
  const [pages, setPages] = useState<Readonly<Record<string, number>>>({});
  const expandedId = chosen !== null && result.targets.some((entry) => entry.target.id === chosen) ? chosen : result.targets.find((entry) => entry.status === "ready")?.target.id ?? null;
  const ready = result.targets.filter((entry) => entry.status === "ready").length;
  const targetLabel = (id: string) => result.targets.find((entry) => entry.target.id === id)?.target.label ?? id;
  const enteredList = entered.length > 0 && (
    <>
      <SectionLabel className="px-3">Entered</SectionLabel>
      <ul role="list" aria-label="Entered slips" className="flex flex-col gap-1.5 pb-2">
        {entered.map((slip) => (
          <li key={slip.id} className={cn("mx-1.5 flex items-center gap-2 rounded-md border border-surface-line pl-3 pr-2", tall ? "h-12" : "h-11")}>
            <span className="flex min-w-0 flex-grow flex-col">
              <span className="truncate text-[13px] font-semibold leading-[18px] text-zinc-300">{targetLabel(slip.targetId)}</span>
              <span className="data-num truncate text-[11px] leading-4 text-zinc-500">{slip.rowIds.length} legs · off the board until {clock(slip.firstStartMs)}</span>
            </span>
            <button type="button" onClick={() => onUnenter(slip.id)} aria-label={`Undo entered ${targetLabel(slip.targetId)}`} className="h-7 rounded-md px-2 text-[12px] font-medium text-zinc-400 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
              Undo
            </button>
          </li>
        ))}
      </ul>
    </>
  );

  if (grading.kind === "ungraded") {
    return (
      <>
        <p className="px-3 py-3 text-[12px] leading-4 text-zinc-500">{noStandardText(sportsbookIdToDisplay(book))}</p>
        {enteredList}
      </>
    );
  }

  return (
    <>
      <div className={cn("flex items-center gap-2 border-b border-surface-line px-3", tall ? "py-2.5" : "py-2")}>
        <span className="flex items-center gap-2 text-[12px] text-zinc-400">
          Min EV
          <Stepper value={minEvPct} min={0} max={30} onChange={onMinEv} label="Minimum slip EV" active={minEvPct > 0} unit="%" />
        </span>
        <span className="flex-grow" />
        {swappedCount > 0 && (
          <button type="button" onClick={onResetSwaps} className="h-7 rounded-md px-2 text-[12px] font-medium text-zinc-400 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
            Reset {swappedCount} {swappedCount === 1 ? "swap" : "swaps"}
          </button>
        )}
      </div>
      <div className="flex items-center justify-between pr-3">
        <SectionLabel className="px-3">Best slips · {ready} of {result.targets.length + result.unpriced.length} types +EV</SectionLabel>
        <span className="flex items-center gap-1.5 text-[11px] text-zinc-500"><span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />live</span>
      </div>
      <div className="flex flex-col gap-1.5 pb-2">
        {result.targets.map((entry) => (entry.target.id === expandedId && entry.slips.length > 0 ? (
          <ExpandedCard
            key={entry.target.id}
            entry={entry}
            page={pages[entry.target.id] ?? 0}
            onPage={(page) => setPages((current) => ({ ...current, [entry.target.id]: page }))}
            book={book}
            rowAt={rowAt}
            events={events}
            format={format}
            onUse={onUse}
            onEnter={onEnter}
            onSwap={onSwap}
            onPreview={onPreview}
            tall={tall}
          />
        ) : (
          <CollapsedRow key={entry.target.id} entry={entry} onExpand={() => setChosen(entry.target.id)} tall={tall} />
        )))}
        {result.unpriced.length > 0 && (
          <div className={cn("mx-1.5 flex items-center gap-2 rounded-md border border-surface-line pl-3 pr-2.5", tall ? "h-12" : "h-11")}>
            <span className="flex min-w-0 flex-grow flex-col">
              <span className="truncate text-[13px] font-semibold leading-[18px] text-zinc-500">{result.unpriced.join(" · ")}</span>
              <span className="truncate text-[11px] leading-4 text-zinc-500">no payout table yet</span>
            </span>
            <span className="text-[11px] text-zinc-500">unpriced</span>
          </div>
        )}
      </div>
      {enteredList}
      <p className="px-3 pb-3 pt-1 text-[11px] leading-4 text-zinc-500">
        Ranked with {modelLabel}{boostPct > 0 ? ` and the ${boostPct}% boost` : ""}. One leg per game, one per player{oneLeague ? ", one league and one promo pick per slip" : ""}. Entered slips drop out until their games start.
      </p>
    </>
  );
}
